import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { useStaticQuery, graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import trackCustomEventTrigger from "utils/trackCustomEventTrigger"
import trackingCategoriesHelper from "utils/trackingCategoriesHelper"

import Blog from "assets/images/blog.svg"
import useFormProgress from "utils/useFormProgress"
import postSubscription from "utils/postSubscription"
import Button from "components/atoms/Button"
import H from "components/atoms/H"
import P from "components/atoms/P"
import Spinner from "components/atoms/Spinner"
import Watchlist from "./WatchlistStep"
import ConfirmationStep from "./ConfirmationStep"

import {
  StyledWrapper,
  StyledContentWrapper,
  StyledFormFooterWrapper,
  StyledButtonWrapper,
  buttonStyles,
  secondaryButton,
  StyledSuccessWrapper,
  StyledCloseButton,
  StyledCloseIcon,
} from "./SubscriptionForm.styles"

const query = graphql`
  query Projects {
    api {
      projects(language: en) {
        id
        bannerImages {
          image
          id
        }
        overview {
          city {
            name
            stateCode
          }
        }
        name
      }
    }
  }
`

const SubscriptionForm = ({ handleClose, defaultProjectId }) => {
  const { formatMessage } = useIntl()
  const [currentStep, goForward, goBack] = useFormProgress()
  const [loaderVisible, setLoaderVisible] = useState(false)
  const [phoneError, setPhoneError] = useState(null)

  const [selectAll, setSelectAll] = useState(false)
  const { api } = useStaticQuery(query)

  const blogBlock = {
    id: "blog",
    name: "Blog posts",
    overview: {},
    selected: false,
    blogIcon: <Blog />,
  }

  const [projects, setProjects] = useState([blogBlock, ...api.projects])
  const selectedNumber =
    projects && projects.filter((proj) => proj.selected).length

  const findProjectName = (id) =>
    projects.find((project) => project.id === id).name

  const toggleSelection = (projectId) => {
    const arrCopy = projects.map((proj) => {
      if (proj.id === projectId) return { ...proj, selected: !proj.selected }
      return proj
    })
    setProjects(arrCopy)

    trackCustomEventTrigger({
      category: trackingCategoriesHelper.SUBSCRIBE,
      action: "Click",
      label: `${trackingCategoriesHelper.SUBSCRIBE} button(${findProjectName(
        projectId
      )})`,
    })
  }

  const moveToFirstPosition = (projectId) =>
    setProjects(
      projects.unshift(
        projects.splice(
          projects.findIndex((proj) => proj.id === projectId),
          1
        )[0]
      )
    )

  useEffect(() => {
    if (defaultProjectId) {
      moveToFirstPosition(defaultProjectId)
      toggleSelection(defaultProjectId)
    }
  }, [defaultProjectId])

  const toggleAll = (e) => {
    e.stopPropagation()
    setSelectAll(!selectAll)
    const arrCopy = projects.map((proj) => {
      const projCopy = { ...proj }
      projCopy.selected = !selectAll || proj.id === defaultProjectId
      return projCopy
    })
    setProjects(arrCopy)

    trackCustomEventTrigger({
      category: trackingCategoriesHelper.SUBSCRIBE,
      action: "Click",
      label: `${trackingCategoriesHelper.SUBSCRIBE} selectAll(${!selectAll})`,
    })
  }
  const sendForm = async (formData) => {
    const projectsIdList = projects
      .filter((proj) => proj.selected)
      .map((project) => project.id)

    setLoaderVisible(true)
    const response = await postSubscription({
      project: projectsIdList,
      ...formData,
    })

    const { errors } = response

    setPhoneError(
      errors && errors[0].extensions.validationErrors[0].phoneNumber
    )
    setLoaderVisible(false)

    if (!errors) {
      goForward()
      trackCustomEventTrigger({
        category: trackingCategoriesHelper.SUBSCRIBE,
        action: "Click",
        label: `${trackingCategoriesHelper.SUBSCRIBE} button(Subscribe)`,
      })
    }
  }

  const steps = [
    <Watchlist
      key="watchlist"
      projectList={projects}
      toggleSelection={toggleSelection}
      toggleAll={toggleAll}
    />,
    <ConfirmationStep
      key="confirmation"
      selectedProjects={projects.filter((proj) => proj.selected)}
      sendForm={sendForm}
      phoneError={phoneError}
    />,
    <StyledSuccessWrapper key="success" data-testid="subscribeSuccessWrapper">
      <H as="h2">{formatMessage({ id: "subscriptionModal.success.title" })}</H>
      <P>{formatMessage({ id: "subscriptionModal.success.message" })}</P>
    </StyledSuccessWrapper>,
  ]

  const buttonList = [
    <>
      <span data-testid="subscribeFooterSelectedNumber">
        {selectedNumber}
        {formatMessage({ id: "subscriptionModal.watchList.selected" })}
      </span>
      <Button
        type="secondary"
        onClick={handleClose}
        extraStyles={secondaryButton}
        data-testid="subscribeFooterCancelButton"
      >
        {formatMessage({ id: "subscriptionModal.watchList.secondaryButton" })}
      </Button>
      <Button
        onClick={() => {
          goForward()
          trackCustomEventTrigger({
            category: trackingCategoriesHelper.SUBSCRIBE,
            action: "Click",
            label: `${trackingCategoriesHelper.SUBSCRIBE} button(Next)`,
          })
        }}
        extraStyles={buttonStyles}
        disabled={!selectedNumber}
        data-testid="subscribeFooterNextButton"
      >
        {formatMessage({ id: "subscriptionModal.watchList.primaryButton" })}
      </Button>
    </>,
    <>
      <Button
        type="secondary"
        onClick={() => {
          goBack()
          trackCustomEventTrigger({
            category: trackingCategoriesHelper.SUBSCRIBE,
            action: "Click",
            label: `${trackingCategoriesHelper.SUBSCRIBE} button(Back)`,
          })
        }}
        extraStyles={secondaryButton}
        data-testid="subscribeFooterBackButton"
      >
        {formatMessage({
          id: "subscriptionModal.confirmationStep.secondaryButton",
        })}
      </Button>
      <Button
        extraStyles={buttonStyles}
        form="subcriptionForm"
        data-testid="subscribeFooterSubscribeButton"
      >
        {formatMessage({
          id: "subscriptionModal.confirmationStep.primaryButton",
        })}
      </Button>
      {loaderVisible && <Spinner />}
    </>,
  ]

  return (
    <StyledWrapper data-testid="subscribeWrapper">
      <StyledCloseButton
        onClick={handleClose}
        data-testid="subscribeCloseButton"
      >
        <StyledCloseIcon />
      </StyledCloseButton>
      <StyledContentWrapper data-testid="subscribeContentWrapper">
        {steps[currentStep]}
      </StyledContentWrapper>
      <StyledFormFooterWrapper>
        <StyledButtonWrapper data-testid="subscribeButtonWrapper">
          {buttonList[currentStep]}
        </StyledButtonWrapper>
      </StyledFormFooterWrapper>
    </StyledWrapper>
  )
}
SubscriptionForm.propTypes = {
  handleClose: PropTypes.func.isRequired,
  defaultProjectId: PropTypes.string,
}

SubscriptionForm.defaultProps = {
  defaultProjectId: null,
}

export default SubscriptionForm
